import { Box, Button, Card, styled, useTheme, useMediaQuery } from '@mui/material';
import { H2 } from 'components/Typography';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next'; // ------------------------------------------------------------------
import { useMemo, useState } from 'react';

const StyledChart = styled(Chart)(({ theme }) => ({
    '& .apexcharts-tooltip *': {
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
    },
    '& .apexcharts-tooltip': {
        boxShadow: 0,
        borderRadius: 4,
        alignItems: 'center',
        '& .apexcharts-tooltip-text-y-value': {
            color: 'primary.main',
        },
        '& .apexcharts-tooltip-title': {
            display: 'block',
            width: '100%',
            backgroundColor: theme.palette.action.hover,
            padding: '6px 10px',
            marginBottom: 6,
        },
        '& .apexcharts-tooltip.apexcharts-theme-light': {
            border: `1px solid ${theme.palette.divider}`,
        },
        '& .apexcharts-tooltip-series-group:last-child': {
            paddingBottom: 0,
        },
    },
})); // ------------------------------------------------------------------

const SponsorSummary = ({
    data,
    title = 'Sponsor Summary',
    maxItems = 15,
    valueUnit = 'Participant',
    seriesName = 'Participants',
    sortByValue = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showAll, setShowAll] = useState(false);
    const pairs = useMemo(() => {
        const values = data?.series?.[0]?.data || [];
        const names = data?.categories || [];
        const combined = names.map((n, i) => ({
            name: n,
            value: parseInt(values[i] || 0),
        }));
        if (sortByValue) {
            combined.sort((a, b) => {
                return b.value - a.value || a.name.localeCompare(b.name);
            });
        }
        return combined;
    }, [data, sortByValue]);
    const MAX = maxItems;
    const canExpand = pairs.length > MAX;
    const visiblePairs = useMemo(() => {
        return showAll && canExpand ? pairs : pairs.slice(0, MAX);
    }, [pairs, showAll, canExpand]);
    const total = useMemo(
        () => pairs.reduce((acc, p) => acc + (isNaN(p.value) ? 0 : p.value), 0),
        [pairs],
    );
    const { t } = useTranslation();
    const chartOptions = {
        chart: {
            background: 'transparent',
            toolbar: {
                show: false,
            },
        },
        colors: [theme.palette.primary.main],
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val}`,
            style: {
                colors: [theme.palette.common.white],
                fontWeight: 600,
            },
        },
        grid: {
            show: false,
        },
        states: {
            active: {
                filter: {
                    type: 'none',
                },
            },
            hover: {
                filter: {
                    type: 'none',
                },
            },
        },
        theme: {
            mode: theme.palette.mode,
        },
        xaxis: {
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            categories: visiblePairs.map((p) => p.name),
            labels: {
                show: true,
                style: {
                    colors: theme.palette.text.disabled,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                },
            },
        },
        yaxis: {
            show: true,
            labels: {
                style: { fontWeight: 500 },
            },
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                horizontal: true,
                barHeight: '32px',
                dataLabels: {
                    position: 'center',
                },
            },
        },
        tooltip: {
            x: {
                show: true,
            },
            y: {
                formatter: (val) => `${val} ${valueUnit}`,
            },
        },
    };
    const chartSeries = [
        {
            name: seriesName,
            data: visiblePairs.map((p) => p.value),
        },
    ];
    const dynamicHeight = Math.min(visiblePairs.length * 44 + 140, 560);
    return (
        <Card
            sx={{
                paddingX: 4,
            }}
        >
            <H2 mt={4} textAlign="center">
                {title}: {total} {valueUnit}
            </H2>
            <Box sx={{ mt: 2 }}>
                {data && !isMobile && (
                    <StyledChart
                        height={dynamicHeight}
                        options={chartOptions}
                        series={chartSeries}
                        type="bar"
                    />
                )}
                {data && isMobile && (
                    <Box sx={{ px: 1 }}>
                        {visiblePairs.map((p) => {
                            const maxVal = Math.max(...pairs.map((x) => x.value), 1);
                            const widthPct = Math.max(
                                Math.round((p.value / maxVal) * 100),
                                3,
                            );
                            return (
                                <Box key={p.name} sx={{ mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: 13,
                                                color: theme.palette.text.secondary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '70%',
                                            }}
                                        >
                                            {p.name}
                                        </Box>
                                        <Box
                                            sx={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {p.value}
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 24,
                                            borderRadius: 8,
                                            backgroundColor: theme.palette.action.focus,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${widthPct}%`,
                                                height: '100%',
                                                borderRadius: 8,
                                                backgroundColor: theme.palette.primary.main,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme.palette.common.white,
                                                fontSize: 12,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {p.value}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
            {canExpand && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowAll((s) => !s)}
                    >
                        {showAll ? t(`Show Top ${MAX}`) : t('Show All')}
                    </Button>
                </Box>
            )}
        </Card>
    );
};

export default SponsorSummary;
